/**
 * AcceptInvitePage - Handle dealer invite acceptance
 * 
 * This page is shown when a user clicks an invite link from their email.
 * It collects their email (for verification) and password to create their account.
 * 
 * NO public registration - all accounts must be created via invite from Master Admin.
 */
import { useState, useEffect } from 'react';
import { API_BASE } from '../api/config';

const AcceptInvitePage = ({ token, onSuccess, onError }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);

  // Validate token on mount (optional - could add a validate endpoint)
  useEffect(() => {
    // For now, just set as valid and let the submit handle invalid tokens
    setIsValidating(false);
    setTokenValid(true);
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password length
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE}/public/auth/accept-invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token,
          password: password,
        }),
      });

      // Handle non-JSON responses gracefully — older 5xx paths used to
      // return plain "Internal Server Error" text which broke
      // `response.json()` with the user-hostile "Unexpected token I" parse
      // error. We now read the body as text first, try to parse JSON, and
      // fall back to a friendly message if it isn't JSON.
      const rawBody = await response.text();
      let data;
      try {
        data = rawBody ? JSON.parse(rawBody) : {};
      } catch (_parseErr) {
        // Non-JSON response (typically a bare 5xx HTML/text page).
        if (response.ok) {
          // Successful but unparseable — treat as success with a generic msg.
          if (onSuccess) onSuccess({ message: 'Account created successfully' });
          return;
        }
        const friendly = response.status >= 500
          ? `Server error (${response.status}). Please try again in a moment or contact support.`
          : `Request failed (${response.status}). ${rawBody.slice(0, 120)}`;
        throw new Error(friendly);
      }

      if (!response.ok) {
        throw new Error(data.detail || data.message || `Failed to accept invitation (${response.status})`);
      }

      // Success - redirect to login
      if (onSuccess) {
        onSuccess(data);
      }
    } catch (err) {
      setError(err.message);
      if (onError) {
        onError(err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isValidating) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="spinner"></div>
          <p>Validating invitation...</p>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <h2>Invalid Invitation</h2>
          <p className="auth-subtitle" style={{ color: '#dc2626' }}>
            This invitation link is invalid or has expired.
          </p>
          <p style={{ marginTop: '16px', color: '#6b7280' }}>
            Please contact your administrator to request a new invitation.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h2>Set Up Your Account</h2>
        <p className="auth-subtitle">
          You&apos;ve been invited to Verified Demand. Enter your email and create a password to activate your account.
        </p>

        {error && (
          <div className="error-message" style={{ 
            background: '#fef2f2', 
            border: '1px solid #fecaca', 
            color: '#dc2626',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '16px'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email Address</label>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0 8px' }}>
              Enter the email address where you received the invitation
            </p>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@example.com"
              data-testid="accept-invite-email"
              style={{ width: '100%' }}
            />
          </div>

          <div className="form-group">
            <label>Create Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              placeholder="At least 8 characters"
              data-testid="accept-invite-password"
              style={{ width: '100%' }}
            />
          </div>

          <div className="form-group">
            <label>Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              placeholder="Re-enter your password"
              data-testid="accept-invite-confirm-password"
              style={{ width: '100%' }}
            />
          </div>

          <button
            type="submit"
            className="btn-primary"
            disabled={isLoading}
            data-testid="accept-invite-submit"
            style={{ width: '100%', marginTop: '8px' }}
          >
            {isLoading ? 'Creating Account...' : 'Activate Account'}
          </button>
        </form>

        <p style={{ 
          marginTop: '24px', 
          fontSize: '12px', 
          color: '#9ca3af', 
          textAlign: 'center' 
        }}>
          By activating your account, you agree to the Verified Demand Terms of Service.
        </p>
      </div>
    </div>
  );
};

export default AcceptInvitePage;
